import React, { useState, useEffect } from "react"
import { Flex, Heading, Text, Button, Box, useColorModeValue, Input, useToast, Icon, Badge, Select, SimpleGrid } from "@chakra-ui/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash, faArrowUp, faArrowDown, faPlus, faMobileScreen, faVolumeHigh, faVolumeMute } from "@fortawesome/free-solid-svg-icons"
import { apiGetRequest, apiPutRequest } from "@/functions/requests"

interface CourseGroupCarouselProps {
    items: any[];
    allCourses: any[];
    mediaUrls: Record<string, string>;
    autoSlideInterval: number;
}

function CourseGroupCarousel({ items, allCourses, mediaUrls, autoSlideInterval }: CourseGroupCarouselProps) {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (items.length <= 1) {
            setActiveIndex(0);
            return;
        }
        
        const interval = setInterval(() => {
            setActiveIndex(prev => (prev + 1) % items.length);
        }, autoSlideInterval * 1000);

        return () => clearInterval(interval);
    }, [items.length, autoSlideInterval]);

    const cardWidth = 220;
    const gap = 12;
    const containerWidth = 268;
    const offset = (containerWidth - cardWidth) / 2 - activeIndex * (cardWidth + gap);

    return (
        <Box py={1} w="100%" overflow="hidden" position="relative">
            <Flex 
                transform={`translateX(${offset}px)`}
                transition="transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)"
                gap={`${gap}px`}
                w="max-content"
                alignItems="center"
            >
                {items.map((item: any, idx: number) => {
                    const course = allCourses.find(c => c.id === item.courseId);
                    const isActive = idx === activeIndex;

                    return (
                        <Flex 
                            key={idx} 
                            direction="column"
                            w={`${cardWidth}px`}
                            minW={`${cardWidth}px`}
                            height="145px"
                            borderRadius="2xl" 
                            overflow="hidden" 
                            shadow={isActive ? "2xl" : "md"}
                            bg="gray.800"
                            border="2px solid"
                            borderColor={isActive ? "green.400" : "gray.700"}
                            opacity={isActive ? 1 : 0.4}
                            transform={isActive ? "scale(1)" : "scale(0.92)"}
                            transition="all 0.6s cubic-bezier(0.16, 1, 0.3, 1)"
                            flexShrink={0}
                            position="relative"
                        >
                            <Flex w="100%" h="100%" align="center" justify="center" bg="gray.850" overflow="hidden">
                                {mediaUrls[item.courseId] ? (
                                    <img 
                                        src={mediaUrls[item.courseId]} 
                                        style={{ 
                                            width: '100%',
                                            height: '100%', 
                                            objectFit: item.cropStyle || 'cover',
                                            objectPosition: `${item.focalX ?? 50}% ${item.focalY ?? 50}%`,
                                            transform: `scale(${item.zoom || 1})`,
                                            transformOrigin: `${item.focalX ?? 50}% ${item.focalY ?? 50}%`,
                                            transition: 'transform 0.2s ease'
                                        }} 
                                        alt="Course Thumbnail" 
                                    />
                                ) : (
                                    <Box display="flex" alignItems="center" justifyContent="center">
                                        <Text color="gray.400" fontSize="11px">Course Icon</Text>
                                    </Box>
                                )}
                            </Flex>
                            <Box 
                                position="absolute" 
                                bottom={0} 
                                left={0} 
                                right={0} 
                                bgGradient="linear(to-t, rgba(0,0,0,0.5), transparent)" 
                                px={2} 
                                pb={1.5} 
                                pt={5}
                            >
                                <Text 
                                    fontWeight="black" 
                                    color="white" 
                                    isTruncated 
                                    textAlign="center" 
                                    fontSize="11px"
                                    lineHeight="short"
                                >
                                    {course?.name || "Select Course"}
                                </Text>
                            </Box>
                        </Flex>
                    )
                })}
            </Flex>

            {items.length > 1 && (
                <Flex justifyContent="center" gap={1.5} mt={3}>
                    {items.map((_, dotIdx) => {
                        const isDotActive = dotIdx === activeIndex;
                        return (
                            <Box 
                                key={dotIdx} 
                                w={isDotActive ? "16px" : "6px"} 
                                h="6px" 
                                borderRadius="full" 
                                bg={isDotActive ? "green.400" : "whiteAlpha.400"} 
                                transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
                            />
                        );
                    })}
                </Flex>
            )}
        </Box>
    );
}

export default function MobileBanner() {
    const [bannerItems, setBannerItems] = useState<any[]>([])
    const [allCourses, setAllCourses] = useState<any[]>([])
    const [savingBanner, setSavingBanner] = useState(false)
    const [uploadingAd, setUploadingAd] = useState(false)
    const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({})
    const [localMediaUrls, setLocalMediaUrls] = useState<Record<string, string>>({})
    const [autoSlideInterval, setAutoSlideInterval] = useState(3)
    const [isAdMuted, setIsAdMuted] = useState(true)

    useEffect(() => {
        return () => {
            Object.values(localMediaUrls).forEach(url => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url)
                }
            })
        }
    }, [localMediaUrls])

    const toast = useToast()
    const bg = useColorModeValue("white", "gray.800")
    const borderColor = useColorModeValue("gray.200", "gray.700")

    useEffect(() => {
        fetchCourses()
        fetchBanner()
    }, [])

    const fetchCourses = async () => {
        try {
            const data = await apiGetRequest<undefined, any[]>('/apps')
            if (data) setAllCourses(data)
        } catch (error) {
            console.error("Error fetching courses", error)
        }
    }

    const fetchBanner = async () => {
        try {
            const data = await apiGetRequest<undefined, { items: any[] }>('/users/mobile_banner')
            if (data && data.items) {
                setBannerItems(data.items)
                const firstCourse = data.items.find(item => item.type === 'course' && item.autoSlideInterval);
                if (firstCourse) {
                    setAutoSlideInterval(Number(firstCourse.autoSlideInterval));
                }
            }
        } catch (error) {
            console.error("Error fetching mobile banner", error)
        }
    }

    useEffect(() => {
        const loadMissingMedia = async () => {
            const newlyResolved: Record<string, string> = {}

            for (const item of bannerItems) {
                if (item.type === 'course' && item.courseId && !mediaUrls[item.courseId] && !newlyResolved[item.courseId]) {
                    try {
                        const url = await apiGetRequest<undefined, string>(`/appIcon/${item.courseId}`)
                        if (url) {
                            newlyResolved[item.courseId] = url
                        }
                    } catch (e) {
                        console.error("Failed to load course icon", e)
                    }
                } else if (item.type === 'ad' && item.fileUrl && !mediaUrls[item.fileUrl] && !newlyResolved[item.fileUrl]) {
                    try {
                        let fileKey = item.fileKey
                        if (!fileKey && item.fileUrl.includes('.amazonaws.com/')) {
                            fileKey = item.fileUrl.split('.amazonaws.com/')[1]
                        }
                        
                        if (fileKey) {
                            const res = await apiGetRequest<undefined, { signedUrl: string }>(`/get-profile-image?mediaKeyId=${encodeURIComponent(fileKey)}`)
                            if (res && res.signedUrl) {
                                newlyResolved[item.fileUrl] = res.signedUrl
                            }
                        } else {
                            newlyResolved[item.fileUrl] = item.fileUrl
                        }
                    } catch (e) {
                        console.error("Failed to load ad signed url", e)
                        newlyResolved[item.fileUrl] = item.fileUrl
                    }
                }
            }

            if (Object.keys(newlyResolved).length > 0) {
                setMediaUrls(prev => ({ ...prev, ...newlyResolved }))
            }
        }

        loadMissingMedia()
    }, [bannerItems])

    const handleFocalShift = (index: number, dx: number, dy: number) => {
        const newItems = [...bannerItems]
        const item = newItems[index]
        const x = Math.min(100, Math.max(0, (item.focalX ?? 50) + dx))
        const y = Math.min(100, Math.max(0, (item.focalY ?? 50) + dy))
        item.focalX = x
        item.focalY = y
        setBannerItems(newItems)
    }

    const handleFocalReset = (index: number) => {
        const newItems = [...bannerItems]
        newItems[index].focalX = 50
        newItems[index].focalY = 50
        setBannerItems(newItems)
    }

    const renderMediaControls = (item: any, index: number) => {
        const zoom = item.zoom || 1;
        const focalX = item.focalX ?? 50;
        const focalY = item.focalY ?? 50;

        return (
            <Box mt={3} pt={3} borderTop="1px dashed" borderColor={borderColor}>
                <SimpleGrid columns={2} spacing={4}>
                    {/* Column 1: Fit Style & Zoom */}
                    <Flex direction="column" gap={3}>
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>CROP/FIT STYLE</Text>
                            <Select size="xs" value={item.cropStyle || 'cover'} onChange={e => handleBannerChange(index, 'cropStyle', e.target.value)} bg={bg}>
                                <option value="cover">Fill (Crop to Fit)</option>
                                <option value="contain">Fit (Show Whole)</option>
                                <option value="fill">Stretch to Distort</option>
                            </Select>
                        </Box>
                        
                        <Box>
                            <Flex justify="space-between" align="center" mb={1}>
                                <Text fontSize="xs" fontWeight="bold" color="gray.500">ZOOM LEVEL</Text>
                                <Text fontSize="xs" color="blue.500" fontWeight="bold">{zoom.toFixed(1)}x</Text>
                            </Flex>
                            <Flex align="center" gap={2}>
                                <Input 
                                    type="range" 
                                    min="1" 
                                    max="3" 
                                    step="0.1" 
                                    value={zoom} 
                                    onChange={e => handleBannerChange(index, 'zoom', Number(e.target.value) as any)}
                                    size="xs"
                                    style={{ height: '18px', cursor: 'pointer' }}
                                />
                            </Flex>
                        </Box>
                    </Flex>

                    {/* Column 2: Focal position pad */}
                    <Flex direction="column" align="center" justify="center">
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2} textAlign="center">
                            FOCAL POINT (X: {focalX}%, Y: {focalY}%)
                        </Text>
                        
                        <Flex direction="column" align="center" gap={1}>
                            <Button size="xs" variant="outline" onClick={() => handleFocalShift(index, 0, -5)} isDisabled={focalY <= 0}>▲</Button>
                            <Flex gap={1} align="center">
                                <Button size="xs" variant="outline" onClick={() => handleFocalShift(index, -5, 0)} isDisabled={focalX <= 0}>◀</Button>
                                <Button size="xs" colorScheme="blue" variant="solid" onClick={() => handleFocalReset(index)}>⟲</Button>
                                <Button size="xs" variant="outline" onClick={() => handleFocalShift(index, 5, 0)} isDisabled={focalX >= 100}>▶</Button>
                            </Flex>
                            <Button size="xs" variant="outline" onClick={() => handleFocalShift(index, 0, 5)} isDisabled={focalY >= 100}>▼</Button>
                        </Flex>
                    </Flex>
                </SimpleGrid>
            </Box>
        );
    }

    const handleSaveBanner = async () => {
        setSavingBanner(true)
        try {
            await apiPutRequest('/users/mobile_banner', { items: bannerItems })
            toast({ title: "Banner Saved", status: "success", position: "top-right" })
        } catch (e) {
            toast({ title: "Error", description: "Failed to save banner", status: "error", position: "top-right" })
        } finally {
            setSavingBanner(false)
        }
    }

    const handleAddCourseToBanner = () => setBannerItems([...bannerItems, { type: 'course', courseId: '', autoSlideInterval }])
    const handleAddAdToBanner = () => setBannerItems([...bannerItems, { type: 'ad', fileUrl: '', linkUrl: '', mediaType: 'image' }])

    const handleIntervalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = Number(e.target.value);
        setAutoSlideInterval(val);
        const newItems = bannerItems.map(item => {
            if (item.type === 'course') {
                return { ...item, autoSlideInterval: val };
            }
            return item;
        });
        setBannerItems(newItems);
    };

    const handleBannerChange = (index: number, field: string, value: string) => {
        const newItems = [...bannerItems]
        newItems[index][field] = value
        setBannerItems(newItems)
    }

    const handleRemoveBannerItem = (index: number) => {
        const newItems = [...bannerItems]
        newItems.splice(index, 1)
        setBannerItems(newItems)
    }

    const moveBannerItem = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return
        if (direction === 'down' && index === bannerItems.length - 1) return
        const newItems = [...bannerItems]
        const temp = newItems[index]
        newItems[index] = newItems[direction === 'up' ? index - 1 : index + 1]
        newItems[direction === 'up' ? index - 1 : index + 1] = temp
        setBannerItems(newItems)
    }

    const handleAdUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return
        const file = e.target.files[0]
        setUploadingAd(true)
        try {
            const res = await apiGetRequest<any, { uploadUrl: string, fileUrl: string, fileKey?: string }>(`/upload-url?fileName=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`)
            if (!res?.uploadUrl) throw new Error("Failed to get upload URL")
            
            const uploadResponse = await fetch(res.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type }
            })
            
            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text()
                console.error("Upload response error", errorText)
                throw new Error("Upload failed with status " + uploadResponse.status)
            }
            
            const newItems = [...bannerItems]
            newItems[index].fileUrl = res.fileUrl
            newItems[index].fileKey = res.fileKey || res.fileUrl.split('.amazonaws.com/')[1]
            newItems[index].mediaType = file.type.startsWith('video/') ? 'video' : 'image'
            
            // Create local object URL for instant high-fidelity preview in dev/prod
            const localUrl = URL.createObjectURL(file)
            setLocalMediaUrls(prev => ({ ...prev, [res.fileUrl]: localUrl }))

            setBannerItems(newItems)
            toast({ title: "Ad Media Uploaded", status: "success", position: "top-right" })
        } catch (error: any) {
            console.error(error)
            toast({ title: "Upload failed", description: error.message || "Failed to upload image/video", status: "error", position: "top-right" })
        } finally {
            setUploadingAd(false)
        }
    }

    return (
        <Flex direction="column" maxW="7xl" mx="auto" w="full" px={8} py={10}>
            <Flex justifyContent="space-between" alignItems="center" mb={8}>
                <Flex alignItems="center">
                    <Icon as={FontAwesomeIcon} icon={faMobileScreen} color="blue.500" boxSize={8} mr={4} />
                    <Heading size="lg" color={useColorModeValue('gray.800', 'white')}>Mobile App Banner</Heading>
                </Flex>
            </Flex>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
                <Box bg={bg} p={8} borderRadius="2xl" shadow="sm" border="1px solid" borderColor={borderColor}>
                    <Flex justifyContent="space-between" alignItems="center" mb={6}>
                        <Heading size="md">Discover Screen Stack</Heading>
                        <Flex gap={3} alignItems="center">
                            <Text fontSize="xs" fontWeight="extrabold" color="gray.500" whiteSpace="nowrap">CAROUSEL TIMER:</Text>
                            <Select size="sm" width="85px" value={autoSlideInterval} onChange={handleIntervalChange} bg={bg} borderRadius="md">
                                <option value={3}>3s</option>
                                <option value={5}>5s</option>
                                <option value={6}>6s</option>
                                <option value={10}>10s</option>
                            </Select>
                            <Button size="sm" onClick={handleAddCourseToBanner} leftIcon={<FontAwesomeIcon icon={faPlus} />}>Add Course</Button>
                            <Button size="sm" colorScheme="blue" onClick={handleAddAdToBanner} leftIcon={<FontAwesomeIcon icon={faPlus} />}>Add Ad</Button>
                        </Flex>
                    </Flex>

                    <Flex direction="column" gap={4}>
                        {bannerItems.map((item, index) => (
                            <Flex key={index} p={4} borderWidth="1px" borderColor={borderColor} borderRadius="xl" gap={4} alignItems="flex-start" bg={useColorModeValue('gray.50', 'whiteAlpha.50')}>
                                <Flex direction="column" gap={1} pt={1}>
                                    <Icon as={FontAwesomeIcon} icon={faArrowUp} cursor={index === 0 ? "not-allowed" : "pointer"} color={index === 0 ? "gray.300" : "gray.600"} onClick={() => moveBannerItem(index, 'up')} />
                                    <Icon as={FontAwesomeIcon} icon={faArrowDown} cursor={index === bannerItems.length - 1 ? "not-allowed" : "pointer"} color={index === bannerItems.length - 1 ? "gray.300" : "gray.600"} onClick={() => moveBannerItem(index, 'down')} />
                                </Flex>
                                
                                <Box flex={1}>
                                    {item.type === 'course' ? (
                                        (() => {
                                            const course = allCourses.find(c => c.id === item.courseId);
                                            return (
                                                <Box>
                                                    <Text fontWeight="semibold" fontSize="sm" mb={2}>
                                                        {course ? `Course Highlight - ${course.name}` : "Course Highlight"}
                                                    </Text>
                                                    <Select value={item.courseId} onChange={e => handleBannerChange(index, 'courseId', e.target.value)} bg={bg}>
                                                        <option value="">Select a course...</option>
                                                        {allCourses.map(c => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </Select>

                                                    {item.courseId && renderMediaControls(item, index)}
                                                </Box>
                                            );
                                        })()
                                    ) : (
                                        <Box>
                                            <Text fontWeight="semibold" fontSize="sm" mb={2}>Advertisement</Text>
                                            <Input placeholder="Advertisement Title / Overlay Text (Optional)" value={item.title || ''} onChange={e => handleBannerChange(index, 'title', e.target.value)} mb={3} bg={bg} />
                                            <Input placeholder="Destination Link URL (e.g. https://...)" value={item.linkUrl} onChange={e => handleBannerChange(index, 'linkUrl', e.target.value)} mb={3} bg={bg} />
                                            
                                            <Flex gap={2} alignItems="center" mb={3}>
                                                <Input type="file" accept="image/*,video/*" display="none" id={`ad-upload-${index}`} onChange={e => handleAdUpload(index, e)} />
                                                <Button as="label" htmlFor={`ad-upload-${index}`} size="sm" cursor="pointer" isLoading={uploadingAd}>
                                                    {item.fileUrl ? "Replace Media" : "Upload Image/Video"}
                                                </Button>
                                                {item.fileUrl && (
                                                    <Badge colorScheme="green">Uploaded</Badge>
                                                )}
                                                {item.mediaType && (
                                                    <Badge colorScheme="purple" textTransform="uppercase">{item.mediaType}</Badge>
                                                )}
                                            </Flex>

                                            {item.fileUrl && (
                                                <Flex direction="column" gap={3}>
                                                    {renderMediaControls(item, index)}
                                                    {item.mediaType === 'video' && (
                                                        <SimpleGrid columns={2} spacing={3} mt={1} pt={2} borderTop="1px dashed" borderColor={borderColor}>
                                                            <Box>
                                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>START SECOND</Text>
                                                                <Input size="xs" type="number" placeholder="e.g. 10" value={item.videoStart || ''} onChange={e => handleBannerChange(index, 'videoStart', e.target.value)} bg={bg} />
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>END SECOND</Text>
                                                                <Input size="xs" type="number" placeholder="e.g. 14" value={item.videoEnd || ''} onChange={e => handleBannerChange(index, 'videoEnd', e.target.value)} bg={bg} />
                                                            </Box>
                                                        </SimpleGrid>
                                                    )}
                                                </Flex>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                
                                <Icon as={FontAwesomeIcon} icon={faTrash} color="red.400" cursor="pointer" mt={1} onClick={() => handleRemoveBannerItem(index)} />
                            </Flex>
                        ))}
                        {bannerItems.length === 0 && (
                            <Text color="gray.500" textAlign="center" py={4}>No items in the banner. Add a course or an advertisement.</Text>
                        )}
                    </Flex>

                    <Flex justifyContent="flex-end" mt={8}>
                        <Button size="lg" colorScheme="blue" isLoading={savingBanner} onClick={handleSaveBanner} borderRadius="xl" px={10}>
                            Save Banner
                        </Button>
                    </Flex>
                </Box>
                
                <Box bg={bg} p={8} borderRadius="2xl" shadow="sm" border="1px solid" borderColor={borderColor}>
                    <Heading size="md" mb={6}>Live Preview</Heading>
                    <Box width="300px" height="600px" mx="auto" borderWidth="8px" borderColor="gray.800" borderRadius="3xl" overflow="hidden" position="relative" bg="gray.900" shadow="xl">
                        <Flex p={4} alignItems="center" justifyContent="center" position="relative">
                            <Text position="absolute" left={4} fontWeight="bold" color="green.400">7D</Text>
                            <Text fontWeight="bold" fontSize="lg" color="green.400">Discover</Text>
                        </Flex>
                        <Box p={4} overflowY="auto" height="calc(100% - 60px)">
                            <Flex direction="column" gap={6}>
                                {(() => {
                                    const groupedItems: any[] = [];
                                    let currentCourseGroup: any[] = [];

                                    bannerItems.forEach((item, originalIndex) => {
                                        if (item.type === 'course') {
                                            currentCourseGroup.push({ ...item, originalIndex });
                                        } else {
                                            if (currentCourseGroup.length > 0) {
                                                groupedItems.push({ type: 'course-group', items: currentCourseGroup });
                                                currentCourseGroup = [];
                                            }
                                            groupedItems.push({ ...item, originalIndex });
                                        }
                                    });
                                    if (currentCourseGroup.length > 0) {
                                        groupedItems.push({ type: 'course-group', items: currentCourseGroup });
                                    }

                                    return groupedItems.map((groupOrAd, index) => {
                                        if (groupOrAd.type === 'course-group') {
                                             return (
                                                 <CourseGroupCarousel 
                                                     key={index}
                                                     items={groupOrAd.items}
                                                     allCourses={allCourses}
                                                     mediaUrls={mediaUrls}
                                                     autoSlideInterval={autoSlideInterval}
                                                 />
                                             );
                                        } else {
                                            const item = groupOrAd;
                                            const previewSrc = localMediaUrls[item.fileUrl] || mediaUrls[item.fileUrl];
                                            
                                            // Handle HTML5 video media fragment for trimming seconds
                                            let finalVideoSrc = previewSrc;
                                            if (item.mediaType === 'video' && finalVideoSrc) {
                                                const start = item.videoStart || '0';
                                                const end = item.videoEnd || '';
                                                finalVideoSrc = `${finalVideoSrc}#t=${start}${end ? `,${end}` : ''}`;
                                            }
 
                                             return (
                                                 <Box key={index} borderRadius="2xl" overflow="hidden" shadow="md" cursor="pointer" position="relative" height="160px">
                                                     {item.mediaType === 'video' ? (
                                                         previewSrc ? (
                                                             <video 
                                                                 key={finalVideoSrc} // Key forces video element to reload when source hash fragments change
                                                                 src={finalVideoSrc} 
                                                                 style={{ 
                                                                     width: '100%', 
                                                                     height: '100%', 
                                                                     objectFit: item.cropStyle || 'cover',
                                                                     objectPosition: `${item.focalX ?? 50}% ${item.focalY ?? 50}%`,
                                                                     transform: `scale(${item.zoom || 1})`,
                                                                     transformOrigin: `${item.focalX ?? 50}% ${item.focalY ?? 50}%`,
                                                                     transition: 'transform 0.2s ease'
                                                                 }} 
                                                                 autoPlay 
                                                                 muted={isAdMuted} 
                                                                 loop 
                                                                 playsInline 
                                                             />
                                                         ) : (
                                                             <Flex height="100%" bg="gray.800" align="center" justify="center" direction="column">
                                                                 <Text color="gray.400" fontSize="xs">Loading Video Ad...</Text>
                                                             </Flex>
                                                         )
                                                     ) : previewSrc ? (
                                                         <img 
                                                             src={previewSrc} 
                                                             style={{ 
                                                                 width: '100%', 
                                                                 height: '100%', 
                                                                 objectFit: item.cropStyle || 'cover',
                                                                 objectPosition: `${item.focalX ?? 50}% ${item.focalY ?? 50}%`,
                                                                 transform: `scale(${item.zoom || 1})`,
                                                                 transformOrigin: `${item.focalX ?? 50}% ${item.focalY ?? 50}%`,
                                                                 transition: 'transform 0.2s ease'
                                                             }} 
                                                             alt="Ad" 
                                                         />
                                                     ) : (
                                                         <Flex height="100%" bg="gray.800" align="center" justify="center" direction="column">
                                                             <Text color="gray.400" fontSize="xs">Loading Ad Space...</Text>
                                                         </Flex>
                                                     )}
                                                     {/* Volume Overlay Button */}
                                                     {item.mediaType === 'video' && previewSrc && (
                                                         <Box 
                                                             position="absolute" 
                                                             bottom={2} 
                                                             right={2} 
                                                             zIndex={10} 
                                                             bg="blackAlpha.700" 
                                                             color="white" 
                                                             borderRadius="full" 
                                                             w="28px" 
                                                             h="28px" 
                                                             display="flex" 
                                                             alignItems="center" 
                                                             justifyContent="center"
                                                             onClick={(e) => {
                                                                 e.stopPropagation();
                                                                 setIsAdMuted(!isAdMuted);
                                                             }}
                                                             _hover={{ bg: "blackAlpha.900", transform: "scale(1.08)" }}
                                                             transition="all 0.2s"
                                                         >
                                                             <Icon 
                                                                 as={FontAwesomeIcon} 
                                                                 icon={isAdMuted ? faVolumeMute : faVolumeHigh} 
                                                                 size="xs" 
                                                             />
                                                         </Box>
                                                     )}
                                                     {/* Overlay Title */}
                                                     {previewSrc && item.title && (
                                                         <Box 
                                                             position="absolute" 
                                                             bottom={0} 
                                                             left={0} 
                                                             right={0} 
                                                             bgGradient="linear(to-t, rgba(0,0,0,0.5), transparent)" 
                                                             px={3} 
                                                             pb={2} 
                                                             pt={7}
                                                         >
                                                             <Text color="white" fontWeight="bold" fontSize="xs" textAlign="left" isTruncated maxW="80%">
                                                                 {item.title}
                                                             </Text>
                                                         </Box>
                                                     )}
                                                     {item.linkUrl && (
                                                         <Box position="absolute" top={2} right={2} bg="blackAlpha.600" px={2} py={1} borderRadius="md">
                                                             <Text fontSize="xs" color="white" fontWeight="bold">Ad</Text>
                                                         </Box>
                                                     )}
                                                 </Box>
                                             );
                                        }
                                    });
                                })()}
                            </Flex>
                        </Box>
                    </Box>
                </Box>
            </SimpleGrid>
        </Flex>
    )
}
